import api from '@/lib/axios';
import type { PaginatedResult } from '@/components/data-table';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export type FeedbackTier = 'need_improvement' | 'basic' | 'good' | 'excellent';

export const FEEDBACK_TIER_OPTIONS: { value: FeedbackTier; label: string }[] = [
  { value: 'need_improvement', label: 'Need Improvement' },
  { value: 'basic', label: 'Basic' },
  { value: 'good', label: 'Good' },
  { value: 'excellent', label: 'Excellent' },
];

export type CoScholasticTerm = 'Term 1' | 'Term 2' | 'Term 3';
export const CO_SCHOLASTIC_TERMS: CoScholasticTerm[] = ['Term 1', 'Term 2', 'Term 3'];

export interface CoScholasticCategory {
  id: string;
  name: string;
  objectives: string[];
  feedbackTiers: { needImprovement: string; basic: string; good: string; excellent: string };
  displayOrder: number;
}

export interface CoScholasticAssessment {
  id: string;
  studentId: string;
  categoryId: string;
  categoryName: string;
  academicYearId: string;
  term: CoScholasticTerm;
  feedbackTier: FeedbackTier;
  assessmentDate: string;
  remarks: string | null;
  assessedBy: string | null;
  assessedByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SetAssessmentData {
  studentId: string;
  categoryId: string;
  academicYearId: string;
  term: CoScholasticTerm;
  feedbackTier: FeedbackTier;
  assessmentDate?: string;
  remarks?: string;
}

export interface BatchAssessmentCategoryData {
  categoryId: string;
  feedbackTier: FeedbackTier;
  remarks?: string;
}

export interface BatchSetAssessmentData {
  studentId: string;
  academicYearId: string;
  term: CoScholasticTerm;
  assessmentDate?: string;
  categories: BatchAssessmentCategoryData[];
}

export const CoScholasticApi = {
  categories: async (): Promise<CoScholasticCategory[]> => unwrap(await api.get('/school/co-scholastic/categories')),

  assessments: async (studentId: string): Promise<CoScholasticAssessment[]> =>
    unwrap(await api.get('/school/co-scholastic/assessments', { params: { student_id: studentId } })),

  setAssessment: async (data: SetAssessmentData): Promise<CoScholasticAssessment> =>
    unwrap(await api.post('/school/co-scholastic/assessments', data)),

  batchSetAssessment: async (data: BatchSetAssessmentData): Promise<CoScholasticAssessment[]> =>
    unwrap(await api.post('/school/co-scholastic/assessments/batch', data)),
};

export interface CreateOrgAssessmentData extends SetAssessmentData {
  schoolId: string;
}

export interface BatchCreateOrgAssessmentData extends BatchSetAssessmentData {
  schoolId: string;
}

export interface OrgCoScholasticAssessmentRow {
  schoolId: string;
  schoolName: string;
  id: string;
  studentId: string;
  studentName: string | null;
  admissionNumber: string | null;
  className: string | null;
  sectionName: string | null;
  categoryId: string;
  categoryName: string;
  academicYearId: string;
  term: CoScholasticTerm;
  feedbackTier: FeedbackTier;
  assessmentDate: string;
  remarks: string | null;
  assessedByName: string | null;
  createdAt: string;
}

export interface ListOrgCoScholasticAssessmentsParams {
  schoolId?: string;
  academicYearId?: string;
  term?: CoScholasticTerm;
  categoryId?: string;
  page?: number;
  limit?: number;
  search?: string;
}

export const OrgCoScholasticApi = {
  categories: async (): Promise<CoScholasticCategory[]> => unwrap(await api.get('/org/reports/co-scholastic/categories')),

  createAssessment: async (data: CreateOrgAssessmentData): Promise<CoScholasticAssessment> =>
    unwrap(await api.post('/org/reports/co-scholastic/assessments', data)),

  batchCreateAssessment: async (data: BatchCreateOrgAssessmentData): Promise<CoScholasticAssessment[]> =>
    unwrap(await api.post('/org/reports/co-scholastic/assessments/batch', data)),

  assessments: async (
    params?: ListOrgCoScholasticAssessmentsParams,
  ): Promise<PaginatedResult<OrgCoScholasticAssessmentRow> & { truncated: boolean }> =>
    unwrap(await api.get('/org/reports/co-scholastic/assessments', { params })),

  removeAssessment: async (id: string, schoolId: string): Promise<void> =>
    unwrap(await api.delete(`/org/reports/co-scholastic/assessments/${id}`, { params: { schoolId } })),
};
